import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Power,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ActionModal from "../components/Modals/ActionModal";
import api from "../../../services/api";
import { Pagination } from "../../../services/helper";

// Assuming you have an api instance (axios or similar)

const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");



export default function Users() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, position: "bottom" });

  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });

  // TanStack Query
  const { data, isLoading } = useQuery({
    queryKey: ["usersList", page, search.trim()],
    queryFn: () => api.getUserList(page, search.trim()),
    enabled: true,
    keepPreviousData: true, // Smooth UX when changing pages/search
  });

  const users = data?.data || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, hasNext: false, hasPrev: false };

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Toggle Status Mutation
  const toggleMutation = useMutation({
    mutationFn: (id) => api.toggleSuspendUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
      setActiveMenu(null);
    },
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
      setDeleteModal({ isOpen: false, user: null });
    },
  });

  const toggleMenu = (id, event) => {
    if (activeMenu === id) {
      setActiveMenu(null);
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const dropdownHeight = 140;
    const spaceBelow = window.innerHeight - rect.bottom;

    let top = rect.bottom + window.scrollY + 5;
    let position = "bottom";

    if (spaceBelow < dropdownHeight) {
      top = rect.top + window.scrollY - dropdownHeight - 5;
      position = "top";
    }

    setMenuPosition({
      top,
      left: rect.right - 160,
      position,
    });

    setActiveMenu(id);
  };

  const handleDelete = (user) => {
    setDeleteModal({ isOpen: true, user });
    setActiveMenu(null);
  };

  const confirmDelete = () => {
    if (deleteModal.user) {
      deleteMutation.mutate(deleteModal.user.id);
    }
  };

  const toggleStatus = (id) => {
    toggleMutation.mutate(id);
  };

  // Close dropdown on outside click / scroll
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenu && !e.target.closest(".dropdown-menu") && !e.target.closest(".dropdown-trigger")) {
        setActiveMenu(null);
      }
    };

    const handleScroll = () => setActiveMenu(null);

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [activeMenu]);

  const filteredUsers = users; // Already filtered on backend

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="bg-gray-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Users
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Manage your community members
          </p>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 md:px-6 py-4 border-b border-gray-200">
          <span className="text-sm font-semibold text-gray-700">All Users</span>

          <div className="relative w-full sm:w-[549px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search here..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 bg-gray-50 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Users
                </th>
                <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Joined
                </th>
                <th className="text-left px-4 md:px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-center px-4 md:px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400 text-sm">
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isActive = user.is_suspended === 0;

                  return (
                    <tr
                      key={user.id}
                      className="border-t border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 md:px-6 py-4 text-sm font-medium text-black whitespace-nowrap flex items-center gap-3">
                        <img
          src={user?.avatar?.startsWith("/uploads") ? `${API_ROOT}${user?.avatar}` : user?.avatar}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        {user.name}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm text-black whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm text-black whitespace-nowrap capitalize">
                        {user.role}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-sm text-black whitespace-nowrap">
                        {new Date(user.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${isActive
                              ? "text-teal-600 bg-teal-50 border-teal-200"
                              : "text-red-500 bg-red-50 border-red-200"
                            }`}
                        >
                          {isActive ? "Active" : "Suspended"}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={(e) => toggleMenu(user.id, e)}
                          className="text-black hover:text-gray-700 transition-colors focus:outline-none dropdown-trigger"
                        >
                          <MoreHorizontal size={24} strokeWidth={2.5} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={setPage} />

      {/* Dropdown Menu */}
      {activeMenu !== null && (
        <div
          className="fixed z-50 dropdown-menu"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          <div
            className="w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1 animate-in fade-in zoom-in-95 duration-100"
            style={{
              transformOrigin: menuPosition.position === "top" ? "bottom" : "top",
            }}
          >
            {(() => {
              const user = users.find((u) => u.id === activeMenu);
              if (!user) return null;

              const isActive = user.is_suspended === 0;

              return (
                <>
                  <button
                    onClick={() => toggleStatus(user.id)}
                    disabled={toggleMutation.isPending}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <Power size={14} />
                    {isActive ? "Deactivate" : "Activate"}
                  </button>

                  <button
                    onClick={() => handleDelete(user)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ActionModal
        isOpen={deleteModal.isOpen}
        onRequestClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={confirmDelete}
        actionType="delete"
        title={deleteModal.user?.name || "this user"}
        itemName="User"
        isLoading={deleteMutation.isPending}
        dangerColor="#DC2626"
        customDescription={`This will permanently delete ${deleteModal.user?.name}'s account and all associated data. This action cannot be undone.`}
      />
    </div>
  );
}